import { useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Wand2 } from "lucide-react";

// Mêmes champs texte que le serveur (catalog.helpers TEXT_FIELDS). Grilles tarifaires : titre seulement (lignes et prix
// jamais envoyés à l'IA — prix dynamiques, venus du catalogue).
const TEXT_FIELDS = ["title", "content", "subtitle", "label", "value", "name", "description", "role", "quote", "author", "milestone", "date_label", "step"];
const PRESETS = ["Plus convaincant", "Plus court et direct", "Ton premium, haut de gamme", "Corriger l'orthographe et la ponctuation"];

/** Textes non vides d'un modèle, avec leur emplacement (section, élément, champ). */
export function collectTexts(sections) {
  const refs = [];
  (sections || []).forEach((s, si) => {
    const grid = s?.type === "pricing" || s?.type === "configurable";
    for (const f of grid ? ["title"] : TEXT_FIELDS) if (typeof s?.[f] === "string" && s[f].trim()) refs.push({ si, f, text: s[f] });
    if (!grid && Array.isArray(s?.items)) s.items.forEach((it, ii) => {
      for (const f of TEXT_FIELDS) if (typeof it?.[f] === "string" && it[f].trim()) refs.push({ si, ii, f, text: it[f] });
    });
  });
  return refs;
}

/** Nouvelles sections : chaque texte remplacé à son emplacement, tout le reste (ids, types, prix…) inchangé. */
export function applyTexts(sections, refs, texts) {
  const next = sections.map((s) => ({ ...s, ...(Array.isArray(s.items) ? { items: s.items.map((it) => ({ ...it })) } : {}) }));
  refs.forEach((r, i) => { (r.ii == null ? next[r.si] : next[r.si].items[r.ii])[r.f] = texts[i]; });
  return next;
}

/** « Améliorer avec l'IA » (modèle d'offre) : consigne facultative → propositions relues avant d'être appliquées. */
export default function ImproveTextsDialog({ sections, onApply, onClose }) {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { refs, texts, kept }

  const propose = async () => {
    const refs = collectTexts(sections);
    if (!refs.length) return toast.error("Aucun texte à améliorer dans ce modèle.");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/improve", { texts: refs.map((r) => r.text), ...(instruction.trim() && { instruction: instruction.trim() }) });
      setResult({ refs, texts: data.texts, kept: data.kept_original || [] });
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  const changes = result ? result.refs.map((r, i) => ({ r, i })).filter(({ r, i }) => result.texts[i] !== r.text) : [];
  const apply = () => {
    onApply(applyTexts(sections, result.refs, result.texts));
    toast.success("Textes améliorés : enregistrez le modèle pour les conserver");
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="improve-dialog">
        <DialogHeader><DialogTitle className="font-heading">Améliorer le modèle avec l'IA</DialogTitle></DialogHeader>
        <DialogDescription>
          L'IA réécrit les textes (titres, paragraphes, listes) sans toucher à la structure ni aux prix. Vous relisez avant d'appliquer.
        </DialogDescription>
        {!result ? (
          <div className="space-y-3 py-2">
            <Textarea value={instruction} maxLength={500} rows={3} onChange={(e) => setInstruction(e.target.value)} data-testid="improve-instruction"
              placeholder="Consigne (facultatif) : ex. mettre en avant la TV, ton plus chaleureux…" className="rounded-sm" />
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => setInstruction(p)} data-testid="improve-preset"
                  className="text-xs px-2.5 py-1 rounded-sm border border-border hover:border-primary hover:text-primary transition-colors">{p}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="improve-result">
            {result.kept.length > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="improve-kept">
                {result.kept.length} texte{result.kept.length > 1 ? "s" : ""} laissé{result.kept.length > 1 ? "s" : ""} tel{result.kept.length > 1 ? "s" : ""} quel{result.kept.length > 1 ? "s" : ""} : l'IA y ajoutait un chiffre (prix, durée…) absent de l'original.
              </p>
            )}
            {changes.length === 0 ? <p className="text-sm text-muted-foreground">Aucune modification proposée.</p> : changes.map(({ r, i }) => (
              <div key={i} className="grid gap-2 sm:grid-cols-2 border-b border-border pb-3" data-testid="improve-change">
                <p className="text-sm whitespace-pre-line text-muted-foreground line-through decoration-muted-foreground/40">{r.text}</p>
                <p className="text-sm whitespace-pre-line">{result.texts[i]}</p>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          {!result ? (
            <Button type="button" onClick={propose} disabled={loading} data-testid="improve-propose" className="rounded-sm bg-primary hover:bg-primary/90">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />} Proposer
            </Button>
          ) : (<>
            <Button type="button" variant="outline" onClick={() => setResult(null)} data-testid="improve-retry" className="rounded-sm">Recommencer</Button>
            <Button type="button" onClick={apply} disabled={!changes.length} data-testid="improve-apply" className="rounded-sm bg-primary hover:bg-primary/90">
              Appliquer {changes.length > 0 ? `(${changes.length})` : ""}
            </Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
